import {
  useAccidentsServiceAccidentsProfessionsList,
  useAccidentsServiceAccidentsRisksList,
  useAccidentsServiceAccidentsSportsList
} from '@insurance/api/queries'
import { RiskSelection } from '@insurance/api/requests'
import { ConditionalText, DateTimeCell, Text } from '@shared/components'
import { useSessionContext } from '@shared/hooks'
import { getFormatCurrency } from '@shared/utils'
import { DescriptionsProps, Divider, Flex, Form } from 'antd'
import { useTranslation } from 'react-i18next'

import { formProps, labelPersonsContractType } from './utils'
import { DetailsWrapper } from '../DetailsWrapper'
import { useAccidentInsuranceValues } from './AccidentInsuranceContext'
import { useStyles } from './styles'
import { AccidentFormValues, ContractTypeCode } from './type'

export const Details = () => {
  const { t, i18n } = useTranslation()
  const { styles } = useStyles()
  // const [showMore, setShowMore] = useState(false)
  const { broker } = useSessionContext()
  const { isEditable, order } = useAccidentInsuranceValues()

  const {
    priceKey,
    insuredAmountProps,
    risksCoveredProps,
    ancillaryRisksCoveredProps,
    contractTypeProps,
    personsCountProps,
    durationProps,
    territoryProps,
    sportRiskProps,
    professionalRiskProps,
    validFromProps,
    effectiveDateProps,
    creditProps
    // risksKey
  } = formProps(t)

  const price = Form.useWatch(priceKey.name, { preserve: true })
  const insuredAmount = Form.useWatch(insuredAmountProps.name, {
    preserve: true
  })
  const risksCovered = Form.useWatch<AccidentFormValues['risks']>(
    risksCoveredProps.name,
    {
      preserve: true
    }
  )
  const ancillaryRisksCovered = Form.useWatch<
    AccidentFormValues['ancillary_risks']
  >(ancillaryRisksCoveredProps.name, {
    preserve: true
  })
  const contractType = Form.useWatch(contractTypeProps.name, {
    preserve: true
  })
  const personsCount = Form.useWatch(personsCountProps.name, {
    preserve: true
  })
  const duration = Form.useWatch(durationProps.name, {
    preserve: true
  })
  const validFrom = Form.useWatch(validFromProps.name, {
    preserve: true
  })
  const territory = Form.useWatch(territoryProps.name, {
    preserve: true
  })
  const sportRisk = Form.useWatch(sportRiskProps.name, {
    preserve: true
  })
  const professionalRisk = Form.useWatch(professionalRiskProps.name, {
    preserve: true
  })
  const effectiveDate = Form.useWatch(effectiveDateProps.name, {
    preserve: true
  })
  const credit = Form.useWatch(creditProps.name, { preserve: true })
  // const risks = Form.useWatch<AccidentFormValues['__risks']>(risksKey.name, {
  //   preserve: true
  // })

  const { data: riskCoveredData } = useAccidentsServiceAccidentsRisksList(
    {
      ordering: 'name'
    },
    [i18n.language]
  )

  const { data: ancillaryRiskCoveredData } =
    useAccidentsServiceAccidentsRisksList(
      {
        ordering: 'name'
      },
      [i18n.language]
    )

  const { data: sportRiskData } = useAccidentsServiceAccidentsSportsList({}, [
    i18n.language
  ])

  const { data: professionalRiskData } =
    useAccidentsServiceAccidentsProfessionsList({}, [i18n.language])

  const risksCoveredLabels = riskCoveredData
    ?.filter(({ code }) => risksCovered?.some((risk) => risk?.risk === code))
    ?.map(({ name, code }) =>
      risksCovered?.some((risk) => risk?.with_deductible && risk.risk === code)
        ? `${name} - ${t('with_deductible').toLowerCase()}`
        : name
    )
    ?.join(', ')

  const ancillaryRisksCoveredLabels = ancillaryRiskCoveredData
    ?.filter(({ code }) =>
      ancillaryRisksCovered?.some((risk: RiskSelection) => risk?.risk === code)
    )
    ?.map(({ name, code }) =>
      ancillaryRisksCovered?.some(
        (risk: RiskSelection) => risk?.with_deductible && risk.risk === code
      )
        ? `${name} - ${t('with_deductible').toLowerCase()}`
        : name
    )
    ?.join(', ')

  const personsCountValue = labelPersonsContractType(t, personsCount)[
    contractType as ContractTypeCode
  ]

  const sportRiskDetails = sportRiskData?.find(({ code }) => code === sportRisk)

  const professionalRiskDetails = professionalRiskData?.find(
    ({ code }) => code === professionalRisk
  )

  const items: DescriptionsProps['items'] = [
    {
      label: insuredAmountProps.label,
      children: getFormatCurrency({ amount: insuredAmount })
    },
    {
      label: risksCoveredProps.label,
      children: (
        <ConditionalText
          condition={!!risksCoveredLabels}
          className={styles.text}
        >
          {risksCoveredLabels}
        </ConditionalText>
      )
    },
    {
      label: ancillaryRisksCoveredProps.label,
      children: (
        <ConditionalText
          condition={!!ancillaryRisksCoveredLabels}
          className={styles.text}
        >
          {ancillaryRisksCoveredLabels}
        </ConditionalText>
      )
    },
    {
      label: contractTypeProps.label,
      children: t(contractType)
    },

    ...(personsCountValue
      ? [
          {
            label: personsCountProps.label,
            children: personsCountValue
          }
        ]
      : []),
    isEditable
      ? {
          label: effectiveDateProps.label,
          children: (
            <DateTimeCell
              dateProps={{ className: styles.date }}
              date={effectiveDate}
              hiddenTime
            />
          )
        }
      : {
          label: validFromProps.label,
          children: (
            <DateTimeCell
              dateProps={{ className: styles.date }}
              date={validFrom}
              hiddenTime
            />
          )
        },
    {
      label: durationProps.label,
      children: (
        <ConditionalText condition={!!duration} className={styles.text}>
          {t(`m${duration}`)}
        </ConditionalText>
      )
    },
    {
      label: territoryProps.label,
      children: t(territory)
    },
    {
      label: sportRiskProps.label,
      children: sportRiskDetails?.name
    },
    {
      label: professionalRiskProps.label,
      children: professionalRiskDetails?.name
    }
  ]

  // const sumRisks = risks?.reduce(
  //   (prev, curr) => {
  //     prev.basePrime += curr.base_prime ? Number(curr.base_prime) : 0
  //     prev.contractTypePrime += curr.contract_type_prime
  //       ? Number(curr.contract_type_prime)
  //       : 0
  //     prev.durationPrime += curr.duration_prime
  //       ? Number(curr.duration_prime)
  //       : 0
  //     prev.territoryPrime += curr.territory_prime
  //       ? Number(curr.territory_prime)
  //       : 0
  //     prev.sportPrime += curr.sport_prime ? Number(curr.sport_prime) : 0
  //     prev.professionPrime += curr.profession_prime
  //       ? Number(curr.profession_prime)
  //       : 0

  //     return prev
  //   },
  //   {
  //     basePrime: 0,
  //     contractTypePrime: 0,
  //     durationPrime: 0,
  //     territoryPrime: 0,
  //     sportPrime: 0,
  //     professionPrime: 0
  //   }
  // )

  // const risksItems: DescriptionsProps['items'] = [
  //   {
  //     label: t('base_tariff'),
  //     children: getFormatCurrency({ amount: sumRisks?.basePrime })
  //   },
  //   {
  //     label: t('contract_type'),
  //     children: getFormatCurrency({ amount: sumRisks?.contractTypePrime })
  //   },
  //   {
  //     label: t('duration'),
  //     children: getFormatCurrency({ amount: sumRisks?.durationPrime })
  //   },
  //   {
  //     label: t('coverage_type'),
  //     children: getFormatCurrency({ amount: sumRisks?.territoryPrime })
  //   },
  //   {
  //     label: t('sport_risk_level'),
  //     children: getFormatCurrency({ amount: sumRisks?.sportPrime })
  //   },
  //   {
  //     label: t('professional_risk_level'),
  //     children: getFormatCurrency({ amount: sumRisks?.professionPrime })
  //   }
  // ]

  const orderPrice = {
    amount: order?.full_paid_amount ? Number(order.full_paid_amount) : 0
  }

  const totalToBePaid = (price?.amount || 0) - (credit || 0)

  const totalToBePaidAfterModification =
    totalToBePaid - (orderPrice?.amount || 0)

  const isMoreThanOrderPrice = totalToBePaidAfterModification > 0

  return (
    <>
      <DetailsWrapper items={items}>
        {price && !isEditable && (
          <>
            {!broker && !!credit && (
              <>
                <Flex align="center" justify="space-between">
                  <Text strong size={18}>
                    {t('order_amount_paid')}
                  </Text>
                  <Text strong size={18} className="text-wrap-nowrap">
                    {getFormatCurrency(price)}
                  </Text>
                </Flex>

                <Flex align="center" justify="space-between">
                  <Text strong size={18}>
                    {t('amount_used_from_balance')}
                  </Text>
                  <Text strong size={18} className="text-wrap-nowrap">
                    {getFormatCurrency({ amount: credit })}
                  </Text>
                </Flex>
              </>
            )}

            <Flex align="center" justify="space-between">
              <Text strong size={18}>
                {t('total_to_be_paid')}
              </Text>
              <Text strong size={18} className="text-wrap-nowrap">
                {getFormatCurrency({ amount: totalToBePaid })}
              </Text>
            </Flex>

            <Divider />
          </>
        )}

        {isEditable && (
          <>
            {orderPrice && (
              <Flex align="center" justify="space-between">
                <Text strong size={18}>
                  {t('amount_paid')}
                </Text>
                <Text strong size={18} className="text-wrap-nowrap">
                  {getFormatCurrency(orderPrice)}
                </Text>
              </Flex>
            )}
            {price && (
              <Flex align="center" justify="space-between">
                <Text strong size={18}>
                  {t('new_amount')}
                </Text>
                <Text strong size={18} className="text-wrap-nowrap">
                  {getFormatCurrency(price)}
                </Text>
              </Flex>
            )}
            {orderPrice && price && (
              <>
                {!broker && !!credit && (
                  <Flex align="center" justify="space-between">
                    <Text strong size={18}>
                      {t('amount_used_from_balance')}
                    </Text>
                    <Text strong size={18} className="text-wrap-nowrap">
                      {getFormatCurrency({ amount: credit })}
                    </Text>
                  </Flex>
                )}

                <Flex align="center" justify="space-between">
                  <Text
                    strong
                    size={22}
                    type={isMoreThanOrderPrice ? 'success' : 'danger'}
                  >
                    {isMoreThanOrderPrice
                      ? t('amount_to_be_paid')
                      : t('amount_to_be_returned')}
                  </Text>
                  <Text
                    strong
                    size={22}
                    type={isMoreThanOrderPrice ? 'success' : 'danger'}
                    className="text-wrap-nowrap"
                  >
                    {getFormatCurrency({
                      amount: totalToBePaidAfterModification,
                      currency: price.currency
                    })}
                  </Text>
                </Flex>
              </>
            )}

            <Divider />
          </>
        )}
      </DetailsWrapper>

      {/* {!!risks && (
        <>
          <Divider>
            <Button type="link" onClick={() => setShowMore((show) => !show)}>
              {t(showMore ? 'show_less' : 'show_more')}
            </Button>
          </Divider>
          {showMore && <DetailsWrapper items={risksItems} />}
        </>
      )} */}
    </>
  )
}
